// components/Capabilities/ImportCapabilities.js
import React, { useState } from 'react';
import apiClient from '../../lib/apiClient';

function ImportCapabilities({ onImportComplete }) {
  const [importing, setImporting] = useState(false);
  const [importStatus, setImportStatus] = useState(null);
  const [previewData, setPreviewData] = useState(null);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      // xlsx is ~350KB minified — loaded on demand at first import, not in main
      const XLSX = await import('xlsx');
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      // Clean and preview data - map to your database fields
      const cleanedData = jsonData.map(row => {
        const cleanRow = {
          name: row['Name'] || row['Capability'] || row['Capability Name'] || row['Capablities'] || '',
          category: row['Category'] || '',
          business_value: row['Business Value'] || row['Business Benefit'] || '',
          description: row['Description'] || '',
          source: row['Source'] || '',
          cal_secure_phase: row['CalSecure Phase'] || row['Cal Secure Phase'] || row['Phase'] || null,
          cisco_product: row['Cisco Product'] || '',
          cisco_security_suite: row['Cisco Security Suite'] || '',
          competitors: row['Competitors'] || '',
          gaps: row['Gaps'] || '',
          cisco_gartner_rating: row["Cisco's Gartner Magic Quadrant Rating"] || row['Cisco Gartner Rating'] || '',
          cisco_forrester_rating: row['Cisco Forrester Wave Quadrant Rating'] || row['Cisco Forrester Rating'] || '',
          gartner_leaders: row["Leaders in Product Category According to Gartner's Magic Quadrant"] || row['Gartner Leaders'] || '',
          forrester_leaders: row['Forrester Wave Leaders'] || row['Forrester Leaders'] || '',
          microsoft_solution: row['Microsoft'] || row['Microsoft Solution'] || '',
          microsoft_pricing: row['Microsoft Pricing'] || '',
          palo_alto_solution: row['Palo Alto'] || row['Palo Alto Solution'] || '',
          palo_alto_pricing: row['Palo Alto Pricing'] || '',
          crowd_strike_solution: row['Crowd Strike'] || row['CrowdStrike'] || row['CrowdStrike Solution'] || '',
          crowd_strike_pricing: row['Crowd Strike Pricing'] || row['CrowdStrike Pricing'] || '',
          fortinet_solution: row['Fortinet'] || row['Fortinet Solution'] || '',
          fortinet_pricing: row['Fortinet Pricing'] || '',
          target_date: row['Target Date'] || null,
          customer_current_product: row['Customer Current Product'] || '',
          customer_current_spend: row['Customer Current Spend'] || null
        };
        
        // Convert phase to number if it exists
        if (cleanRow.cal_secure_phase) {
          cleanRow.cal_secure_phase = parseInt(cleanRow.cal_secure_phase);
        }
        
        // Convert spend to number if it exists
        if (cleanRow.customer_current_spend) {
          cleanRow.customer_current_spend = parseFloat(cleanRow.customer_current_spend.toString().replace(/[$,]/g, ''));
        }
        
        return cleanRow;
      }).filter(row => row.name); // Filter out rows without names

      setPreviewData(cleanedData);
      setImportStatus({
        type: 'preview',
        message: `Ready to import ${cleanedData.length} capabilities. Review the data below and click "Confirm Import" to proceed. All available columns will be imported.`
      });
    } catch (error) {
      setImportStatus({
        type: 'error',
        message: `Error reading file: ${error.message}`
      });
    }
  };

  const confirmImport = async () => {
    if (!previewData) return;

    setImporting(true);
    setImportStatus({
      type: 'info',
      message: 'Importing capabilities...'
    });

    let successCount = 0;
    let errorCount = 0;
    const errors = [];

    for (const capability of previewData) {
      try {
        // Check if capability exists by name
        const existingCaps = await apiClient.get('/capabilities');
        const existing = existingCaps.find(cap => 
          cap.name.toLowerCase() === capability.name.toLowerCase()
        );
        
        if (existing) {
          // Update existing capability
          try {
            await apiClient.put(`/capabilities/${existing.capability_id}`, capability);
            successCount++;
          } catch (updateErr) {
            errorCount++;
            errors.push(`Failed to update: ${capability.name}`);
          }
        } else {
          // Create new capability
          try {
            await apiClient.post('/capabilities', capability);
            successCount++;
          } catch (createErr) {
            errorCount++;
            errors.push(`Failed to create: ${capability.name}`);
          }
        }
      } catch (error) {
        errorCount++;
        errors.push(`Error with ${capability.name}: ${error.message}`);
      }
    }

    setImporting(false);
    setImportStatus({
      type: errorCount > 0 ? 'warning' : 'success',
      message: `Import complete! Successfully imported ${successCount} capabilities. ${errorCount} errors.`,
      errors: errors.length > 0 ? errors : null
    });

    if (onImportComplete) {
      onImportComplete();
    }
  };

  const resetImport = () => {
    setPreviewData(null);
    setImportStatus(null);
    // Reset file input
    const fileInput = document.getElementById('capability-file-input');
    if (fileInput) fileInput.value = '';
  };

  return (
    <div className="import-capabilities-container">
      <h3 className="import-capabilities-title">Import Capabilities from Excel</h3>
      
      {!previewData && (
        <div className="import-file-section">
          <input
            id="capability-file-input"
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileUpload}
            className="import-file-input"
          />
          <label 
            htmlFor="capability-file-input"
            className="import-file-label"
          >
            📁 Choose Excel File
          </label>
          <p className="import-file-hint">
            The importer will automatically map Excel columns to all database fields
          </p>
        </div>
      )}

      {importStatus && (
        <div className={`import-status ${importStatus.type}`}>
          <p>{importStatus.message}</p>
          {importStatus.errors && (
            <ul>
              {importStatus.errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {previewData && (
        <>
          <div className="import-preview-section">
            <h4 className="import-preview-title">
              Preview Data (First 5 Records)
            </h4>
            <div className="import-preview-container">
              <table className="import-preview-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Category</th>
                    <th>Source</th>
                    <th>Phase</th>
                    <th>Cisco Product</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.slice(0, 5).map((row, index) => (
                    <tr key={index}>
                      <td>{row.name}</td>
                      <td>{row.category}</td>
                      <td>{row.source}</td>
                      <td>{row.cal_secure_phase || '-'}</td>
                      <td>{row.cisco_product || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {previewData.length > 5 && (
              <p className="import-preview-note">
                ... and {previewData.length - 5} more records
              </p>
            )}
          </div>

          <div className="import-actions">
            <button
              onClick={confirmImport}
              disabled={importing}
              className="import-button import-button-confirm"
            >
              {importing ? 'Importing...' : 'Confirm Import'}
            </button>
            <button
              onClick={resetImport}
              disabled={importing}
              className="import-button import-button-cancel"
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default ImportCapabilities;